import { RiStackLine, Stack } from '@landler/component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IndicatorName,
  LayerFactValue,
  MAP_LAYER_FACTS,
  MapRastersFact,
  ThreatType,
} from '@/api/rest/resources/types/fact';
import { WidgetSlot, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { DEFAULT_MAP_STYLE, LIGHT_MAP_STYLE } from '@/config/constants';
import { useIsFactInPurchasedIndicatorProducts } from '@/hooks/useIsFactInPurchasedIndicatorProducts';
import { useIsPlotPage } from '@/hooks/useIsPlotPage';
import { useLandscapeView } from '@/hooks/useLandscapeView';
import { useProtectedAreasMethodology } from '@/hooks/useProtectedAreasMethodology';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import {
  HABITAT_INTACTNESS_THREAT_LAYER_OPTIONS,
  LANDCOVER_VIEW_LAYERS,
  LANDSCAPE_VIEW_LAYERS,
  LAYER_OPTIONS,
  LayerProps,
  LEGEND_TYPE,
  LegendConfig,
  MAP_CATEGORY,
  MAP_TYPE,
  MapCategory,
  MapFilter,
  SIEMENS_HACK_PROTECTED_AREAS_LAYER,
  SUBSCRIBED_LAYERS,
} from './constants';
import { DropdownMapFilter } from './DropdownMapFilter';
import { HabitatIntactnessThreatLayers } from './HabitatIntactnessThreatLayers';
import { HabitatIntactnessThreatsPicker } from './HabitatIntactnessThreatsPicker';
import { useAddFactMapLayers, useAddOrgFactMapLayers } from './hooks/useAddFactMapLayer';
import {
  getDefaultOpacityByLayer,
  isLayerValueSelected,
  MAP_BACKGROUND,
  useControlsContext,
  useMapSelectedLayer,
} from './hooks/useFormControls';
import { useIsSiemensOrganization } from './hooks/useIsSiemensOrganization';
import {
  DEFAULT_MAP_LAYER_FILTER,
  MapLayerFilter,
  SetMapLayerFilter,
  useMapLayerFilter,
} from './hooks/useMapLayerFilter';
import { CategoryLegend, IndexLegend, Legend, LegendContainer, LegendItem } from './Legend';
import { GeojsonSourceLayer, RasterSourceLayer } from './MapLayer';
import { MediaMapLayerRenderer } from './MediaMap/MediaMapLayerRenderer';
import { YearSlider } from './YearSlider';

const PlotViewLayerPicker = ({
  layers,
  setLegendFilter,
}: {
  layers: LayerProps[];
  setLegendFilter: SetMapLayerFilter;
}) => {
  const { isActive: isLandscapeViewActive } = useLandscapeView();
  const { hasIbat, hasOsm } = useProtectedAreasMethodology();

  // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
  const isSiemensOrganization = useIsSiemensOrganization();

  let filteredLayers = layers.filter(
    (opt) =>
      !LANDSCAPE_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !LANDCOVER_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !SUBSCRIBED_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !opt.value.includes(MAP_LAYER_FACTS.in_situ_map_layer),
  );

  const hasLandCoverProduct = useIsFactInPurchasedIndicatorProducts(IndicatorName.landcover);
  const hasThreatenedSpeciesProduct = useIsFactInPurchasedIndicatorProducts(
    MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
  );

  filteredLayers = useAddFactMapLayers(filteredLayers, SUBSCRIBED_LAYERS);

  if (!isLandscapeViewActive) {
    return <LayerPicker layers={filteredLayers} setLegendFilter={setLegendFilter} />;
  }

  if (hasLandCoverProduct) {
    const landCoverLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.combined_ibat_lulc_urls);
    if (landCoverLayer) {
      filteredLayers.push(landCoverLayer);
    }
  }

  if (hasThreatenedSpeciesProduct) {
    const threatenedSpeciesLayer = LAYER_OPTIONS.find(
      (opt) => opt.value === MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
    );
    if (threatenedSpeciesLayer) {
      filteredLayers.push(threatenedSpeciesLayer);
    }
  }

  if (hasIbat) {
    const ibatProtectedAreasLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.iucn_protected_areas);
    if (ibatProtectedAreasLayer) {
      filteredLayers.push(ibatProtectedAreasLayer);
    }

    // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
    if (isSiemensOrganization) {
      filteredLayers.push(SIEMENS_HACK_PROTECTED_AREAS_LAYER as LayerProps);
    }
  }

  if (hasOsm) {
    const osmProtectedAreasLayer = LAYER_OPTIONS.find((opt) => opt.value === MAP_LAYER_FACTS.opendata_protected_areas);
    if (osmProtectedAreasLayer) {
      filteredLayers.push(osmProtectedAreasLayer);
    }
  }

  return <LayerPicker layers={filteredLayers} setLegendFilter={setLegendFilter} />;
};

const GeneralViewLayerPicker = ({
  layers,
  setLegendFilter,
}: {
  layers: LayerProps[];
  setLegendFilter: SetMapLayerFilter;
}) => {
  let filteredLayers = layers.filter(
    (opt) =>
      !LANDSCAPE_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !LANDCOVER_VIEW_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !SUBSCRIBED_LAYERS.includes(opt.value as MAP_LAYER_FACTS) &&
      !opt.value.includes(MAP_LAYER_FACTS.in_situ_map_layer),
  );

  filteredLayers = useAddOrgFactMapLayers(filteredLayers, [
    MAP_LAYER_FACTS.iucn_get_level_2_biome_raster_asset,
    MAP_LAYER_FACTS.iucn_get_level_3_efg_raster_asset,
    MAP_LAYER_FACTS.combined_ibat_lulc_urls,
  ]);

  return <LayerPicker layers={filteredLayers} setLegendFilter={setLegendFilter} />;
};

const useMapAvailableThreats = () => {
  const plotFacts = usePlotFacts<MapRastersFact>([MAP_LAYER_FACTS.habitat_intactness_raster_urls]);
  const rasterUrls = getFactFromFactArray<LayerFactValue>(
    plotFacts,
    MAP_LAYER_FACTS.habitat_intactness_raster_urls,
  )?.value;

  const availableThreats = rasterUrls ? (Object.keys(rasterUrls) as ThreatType[]) : [];

  return availableThreats.filter((t) => ThreatType?.[`${t}`] && t !== ThreatType.no_threat_area);
};

const useMapSelectedThreats = () => {
  const controlsForm = useControlsContext();
  return controlsForm.watch('habitatIntactnessThreats');
};

const HabitatIntactnessThreatLayersPicker = () => {
  const selectedLayers = useMapSelectedLayer();
  const HILayer = selectedLayers.find((opt) => opt.value === MAP_LAYER_FACTS.habitat_intactness_raster_urls);

  const threats = useMapAvailableThreats();
  const selectedThreats = useMapSelectedThreats();

  const controlsForm = useControlsContext();
  const setSelectedThreats = (newThreats: ThreatType[]) =>
    controlsForm.setValue('habitatIntactnessThreats', newThreats);

  if (isNullish(HILayer)) {
    return null;
  }

  return (
    <HabitatIntactnessThreatsPicker
      threats={threats}
      selectedThreats={selectedThreats}
      setSelectedThreats={setSelectedThreats}
    />
  );
};

const LayerPicker = ({ layers, setLegendFilter }: { layers: LayerProps[]; setLegendFilter?: SetMapLayerFilter }) => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();
  const selectedLayers = controlsForm.watch('layers');

  const defaultLayersOpacity = getDefaultOpacityByLayer();

  const inSituWidget: WidgetSlot = {
    name: 'in_situ_map_layers',
    type: WidgetType.o_media_map_form_items,
    visible: true,
  };

  const sortedCategories = Object.values<MapCategory>(MAP_CATEGORY).sort((a, b) =>
    t(`shared.projects.project.heatMapLayerSections.${a}`).localeCompare(
      t(`shared.projects.project.heatMapLayerSections.${b}`),
    ),
  );

  const buildLayerWidget = (opt: LayerProps): WidgetSlot => ({
    name: 'heat_map_layer_label',
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {
      className: `gap-4 flex flex-row justify-between text-text-secondary${
        selectedLayers?.includes(opt.value) && !selectedLayers?.includes('unknown') ? ' text-text-primary' : ''
      }`,
    },
    widgets: [
      {
        name: `${opt.value}_checkbox_item`,
        type: WidgetType.a_stack,
        relatedData: {
          className: 'flex flex-row',
        },
        visible: true,
        widgets: [
          {
            name: opt.value,
            type: WidgetType.o_form_checkbox,
            visible: true,
            relatedData: {
              key: opt.value,
              text: t(`shared.projects.project.heatMapLayers.${opt.value as MAP_LAYER_FACTS}.title`),
              value: opt.value,
              formName: 'layers',
              onChange: () => {
                if (setLegendFilter) {
                  setLegendFilter(DEFAULT_MAP_LAYER_FILTER);
                }
                controlsForm.setValue('habitatIntactnessThreats', []);
                controlsForm.setValue('layerOpacity', defaultLayersOpacity);
              },
            },
          },
          {
            name: 'map_layer_tooltip',
            type: WidgetType.m_tooltip,
            visible: true,
            relatedData: {},
            widgets: [
              {
                name: 'map_layer_tooltip_trigger',
                type: WidgetType.m_tooltip_trigger,
                visible: true,
                widgets: [
                  {
                    name: 'map_layer_tooltip_icon',
                    type: WidgetType.a_icon,
                    visible: true,
                    relatedData: {
                      icon: 'ri-information-line',
                      className: 'ml-1 text-active-54 typography-body1',
                    },
                  },
                ],
              },
              {
                name: 'map_layer_header_tooltip_content',
                type: WidgetType.m_tooltip_content,
                visible: true,
                widgets: [
                  {
                    name: 'map_layer_header_tooltip_text',
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      i18nPath: `shared.projects.project.heatMapLayers.${opt.value}.tooltip.body`,
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'map_layer_opacity',
        type: WidgetType.o_map_layer_opacity,
        visible: opt.value === MAP_LAYER_FACTS.unknown ? false : (selectedLayers?.includes(opt.value) ?? false),
        relatedData: {
          layer: opt.value,
        },
      },
    ],
  });

  const sectionWidgets = sortedCategories.flatMap((section) => {
    const sectionLayers = layers.filter((l) => (l.section ?? MAP_CATEGORY.OTHERS) === section);

    if (sectionLayers.length === 0) {
      return [];
    }

    return {
      name: `heat_map_layer_section_${section}`,
      type: WidgetType.a_stack,
      visible: true,
      relatedData: { className: 'gap-4' },
      widgets: [
        {
          name: `heat_map_layer_section_${section}_title`,
          type: WidgetType.a_text,
          visible: true,
          relatedData: {
            i18nPath: `shared.projects.project.heatMapLayerSections.${section}`,
            className: 'typography-body2Semibold text-text-primary',
          },
        },
        ...sectionLayers.sort((a, b) => a.value.localeCompare(b.value)).map(buildLayerWidget),
      ],
    };
  });

  const PICKER_SLOT_CONFIG: WidgetSlotConfiguration = {
    name: 'default_heat_map_layer_picker',
    type: WidgetType.o_map_layer,
    widgets: [
      {
        name: 'heat_map_layer_picker',
        type: WidgetType.o_form_picker,
        visible: true,
        relatedData: {},
        widgets: [
          {
            name: 'heat_map_layer_picker_trigger',
            type: WidgetType.o_form_picker_trigger,
            visible: layers.length > 0,
            relatedData: {
              leftAdornment: <RiStackLine />,
              'data-testid': 'layer-picker',
            },
          },
          {
            name: 'heat_map_layer_picker_content',
            type: WidgetType.o_form_picker_content,
            visible: true,
            widgets: [
              {
                name: 'heat_map_layer',
                type: WidgetType.a_stack,
                visible: true,
                relatedData: { className: 'gap-4' },
                widgets: [
                  ...sectionWidgets,
                  inSituWidget,
                  {
                    name: 'footer_section',
                    type: WidgetType.a_stack,
                    visible: true,
                    relatedData: { className: 'w-full items-end' },
                    widgets: [
                      {
                        name: 'clear_layers_button',
                        type: WidgetType.o_c_clear_layers_button,
                        visible: true,
                        relatedData: { setLegendFilter },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return <WidgetSlotRenderer nestedWidget={PICKER_SLOT_CONFIG} />;
};

const MapMenuItems = ({
  layers,
  setLegendFilter,
  setMapFilter,
  mapFilter,
}: {
  layers: LayerProps[];
  setLegendFilter: SetMapLayerFilter;
  setMapFilter: SetMapLayerFilter;
  mapFilter: MapLayerFilter;
}) => {
  const isPlotPage = useIsPlotPage();
  const selectedLayers = useMapSelectedLayer();
  const allowsLayerFilters = selectedLayers.some((layer) => layer.layerFilter);
  const firstLayerWithFilter = selectedLayers.find((layer) => layer.layerFilter);

  return (
    <Stack direction='row' spacing={2} className='absolute left-6 top-6' data-testid='map-menu-items'>
      {isPlotPage ? (
        <>
          <PlotViewLayerPicker layers={layers} setLegendFilter={setLegendFilter} />
          <HabitatIntactnessThreatLayersPicker />
        </>
      ) : (
        <GeneralViewLayerPicker layers={layers} setLegendFilter={setLegendFilter} />
      )}
      {allowsLayerFilters && firstLayerWithFilter && (
        <DropdownMapFilter
          filter={firstLayerWithFilter.layerFilter as MapFilter}
          setMapFilter={setMapFilter}
          mapFilter={mapFilter}
        />
      )}
      <YearSlider />
    </Stack>
  );
};

export const HeatMapLayer = ({
  setMapStyle,
  layers = LAYER_OPTIONS,
}: {
  setMapStyle: (style: string) => void;
  layers?: LayerProps[];
}) => {
  const { filter: legendFilter, setFilter: setLegendFilter } = useMapLayerFilter();
  const { filter: mapFilter, setFilter: setMapFilter } = useMapLayerFilter();

  const selectedLayers = useMapSelectedLayer();
  const controlsForm = useControlsContext();

  const threatLayers = controlsForm.watch('habitatIntactnessThreats');
  const mapBackground = controlsForm.watch('mapBackground');

  const HILayer = selectedLayers.find((opt) => opt.value === MAP_LAYER_FACTS.habitat_intactness_raster_urls);
  const shouldRenderThreatLayers = threatLayers.length > 0 && !isNullish(HILayer);

  useEffect(() => {
    setMapStyle(mapBackground === MAP_BACKGROUND.satellite ? DEFAULT_MAP_STYLE : LIGHT_MAP_STYLE);
  }, [mapBackground, setMapStyle]);

  if (selectedLayers.every((layer) => layer.value === MAP_LAYER_FACTS.unknown)) {
    return (
      <MapMenuItems
        layers={layers}
        setLegendFilter={setLegendFilter}
        setMapFilter={setMapFilter}
        mapFilter={mapFilter}
      />
    );
  }

  return (
    <>
      <MapMenuItems
        layers={layers}
        setLegendFilter={setLegendFilter}
        setMapFilter={setMapFilter}
        mapFilter={mapFilter}
      />
      {shouldRenderThreatLayers && <HabitatIntactnessThreatLayers threatLayers={threatLayers} />}
      <MapSourceLayer legendFilter={legendFilter} mapFilter={mapFilter} hideHILayer={shouldRenderThreatLayers} />
      <Stack className='absolute left-6 bottom-6 flex flex-col gap-2'>
        <MapLegend
          setLegendFilter={setLegendFilter}
          legendFilter={legendFilter}
          hideHILayer={shouldRenderThreatLayers}
        />
      </Stack>
    </>
  );
};

const MapSourceLayer = withSuspenseBoundary(
  ({
    legendFilter,
    mapFilter,
    hideHILayer,
  }: {
    legendFilter: MapLayerFilter;
    mapFilter: MapLayerFilter;
    hideHILayer: boolean;
  }) => {
    const selectedLayers = useMapSelectedLayer();
    const finalLayers = hideHILayer
      ? selectedLayers.filter((layer) => layer.value !== MAP_LAYER_FACTS.habitat_intactness_raster_urls)
      : selectedLayers;
    return (
      <>
        {finalLayers.map((layer) => {
          switch (layer.type) {
            case MAP_TYPE.raster:
              return <RasterSourceLayer layer={layer} legendFilter={legendFilter} mapFilter={mapFilter} />;
            case MAP_TYPE.geojson:
              return <GeojsonSourceLayer layer={layer} legendFilter={legendFilter} />;
            case MAP_TYPE.inSitu:
              return <MediaMapLayerRenderer />;
            default:
              return null;
          }
        })}
      </>
    );
  },
  null,
);

const MapLegend = ({
  setLegendFilter,
  legendFilter,
  hideHILayer,
}: {
  setLegendFilter: SetMapLayerFilter;
  legendFilter: MapLayerFilter;
  hideHILayer: boolean;
}) => {
  const controlsForm = useControlsContext();
  const layers = controlsForm.watch('layers');

  const selectedLayers = LAYER_OPTIONS.filter((opt) => isLayerValueSelected(layers, opt.value));
  const finalLayers = hideHILayer
    ? selectedLayers.filter((layer) => layer.value !== MAP_LAYER_FACTS.habitat_intactness_raster_urls)
    : selectedLayers;

  if (!finalLayers || finalLayers.length === 0) {
    return null;
  }

  const legends = finalLayers.flatMap((layer) => {
    if (layer.type === MAP_TYPE.inSitu || Array.isArray(layer.legends) === false || layer?.legends?.length === 0) {
      return [];
    }
    return layer.legends?.map((legend) => ({ ...legend, layerLabel: legend.layerLabel ?? layer.value }));
  }) as LegendConfig[];

  return (
    <Stack className='flex flex-col gap-1 max-h-[400px] overflow-y-auto hide-scrollbar'>
      {legends.map((legend, index) => (
        <Legend
          key={legend?.layerLabel ?? ''}
          id={legend?.layerLabel ?? ''}
          layerLabel={legend?.layerLabel ?? undefined}
        >
          <Stack spacing={1} className='bg-white-100'>
            <div key={index}>
              {legend.type === LEGEND_TYPE.numeric && <IndexLegend legend={legend} />}
              {legend.type === LEGEND_TYPE.category && (
                <CategoryLegend legend={legend} setLegendFilter={setLegendFilter} legendFilter={legendFilter} />
              )}
            </div>
          </Stack>
        </Legend>
      ))}
      {hideHILayer && <ThreatLayersLegend />}
    </Stack>
  );
};

const ThreatLayersLegend = () => {
  const controlsForm = useControlsContext();
  const selectedThreatLayers = controlsForm.watch('habitatIntactnessThreats');

  const threatLayers = HABITAT_INTACTNESS_THREAT_LAYER_OPTIONS.filter(
    (layer) => layer?.layerSubType && selectedThreatLayers.includes(layer.layerSubType as ThreatType),
  );

  if (threatLayers.length === 0) {
    return null;
  }

  const legends = threatLayers
    .map((layer) => layer.legends)
    .flat()
    .filter(Boolean) as LegendConfig[];

  return (
    <Legend id='habitat_intactness_raster_urls' layerLabel={MAP_LAYER_FACTS.habitat_intactness_raster_urls}>
      <LegendContainer>
        {legends.map((legend) => {
          return legend.type === LEGEND_TYPE.category && legend.values[0] ? (
            <div key={legend.values[0].text}>
              <LegendItem legendItem={legend.values[0]} />
            </div>
          ) : null;
        })}
      </LegendContainer>
    </Legend>
  );
};
